/**
 * §18.7 — the W-2/W-3 worksheet: the figures to copy onto each employee's Form
 * W-2 and the Form W-3 that totals them, for one year (payroll stage 4).
 *
 * A WORKSHEET, AND IT SAYS SO FIRST, as the Form 941 and TWC worksheets do. The
 * boxes are GET /payroll/forms/w2's (apps/api/src/payroll/w2.ts, from the
 * General Instructions for Forms W-2 and W-3 (2026)); this screen lays them out
 * box by box in the form's own words, and files nothing.
 *
 * WHAT IT CANNOT FILL IS SAID, box by box. Box a is the employee's Social
 * Security number, which no payroll response carries, so the row leads to the
 * employee's page. Boxes b and c carry the business's EIN and address from
 * Settings' Business Details (issue #44), the same ones the filled Form 941
 * carries, as the instructions ask; one not entered there says so and links to
 * it. The boxes this business leaves blank say so, and why.
 *
 * AND THE IRS'S OWN FORM, FILLED (issue #46). Each employee's section offers
 * Download Filled Form W-2: the IRS's PDF with Copies B, C and 2 (for the
 * employee) and D (the business's) filled, box a showing the last four digits
 * only, and Copy A left blank, since the SSA cannot scan a downloaded one --
 * it is filed through the SSA's Business Services Online from the boxes below.
 * A refusal (the year not over, a draft run, no SSN on file) is the server's
 * sentence under the button.
 *
 * BOX E IS ITS THREE SUB-BOXES, a row each (round 5's R5-1): the instructions'
 * common errors name a name misformatted there, and a suffix copied into the
 * surname is the easy one to make from a single string.
 *
 * THE YEAR SO FAR IS SAID TO BE, until 31 December: pay still to come will
 * change it. And the instructions' checks a form fails come before its boxes.
 *
 * The year is kept in the address and chosen from the server's years, as the
 * year to date's is.
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type W2Boxes, type W2Form, type W2Report } from '../api.js';
import { payCounted } from '../lib/labels.js';
import { formatCents } from '../lib/money.js';
import { settingsTab } from '../lib/settings-tabs.js';

/** Boxes 1 to 6, in the form's own words. */
const AMOUNT_BOXES: Array<[string, string, keyof W2Boxes]> = [
  ['1', 'Wages, tips, other compensation', 'box1Cents'],
  ['2', 'Federal income tax withheld', 'box2Cents'],
  ['3', 'Social security wages', 'box3Cents'],
  ['4', 'Social security tax withheld', 'box4Cents'],
  ['5', 'Medicare wages and tips', 'box5Cents'],
  ['6', 'Medicare tax withheld', 'box6Cents'],
];

const NO_STATE_TAX = 'Leave blank: Texas has no state income tax';

/** A box, its words on the form, and what to enter; `amount` right-aligns a figure. */
type Row = [box: string, label: string, entry: ReactNode, amount?: boolean];

function Boxes({ label, rows }: { label: string; rows: Row[] }) {
  return (
    <div className="table-scroll">
      <table className="lines" aria-label={label}>
        <thead>
          <tr>
            <th>Box</th>
            <th>On the Form</th>
            <th>Enter</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([box, text, entry, amount]) => (
            <tr key={`${box} ${text}`}>
              <td>{box}</td>
              <td>{text}</td>
              <td className={amount ? 'num' : undefined}>{entry}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function amountRows(boxes: W2Boxes): Row[] {
  return AMOUNT_BOXES.map(([box, text, key]) => [box, text, formatCents(boxes[key]), true]);
}

export function W2Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [report, setReport] = useState<W2Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getW2Worksheet(year)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the worksheet.');
      });
    return () => {
      live = false;
    };
  }, [year]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  // The year in the address, so the choice on screen is never one year while the page reads another.
  const shown = year ?? report?.year;
  const choices = report === null ? [] : [...new Set([...report.years, ...(shown === undefined ? [] : [shown])])].sort((a, b) => b - a);

  return (
    <section className="w2">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        W-2/W-3 Worksheet{' '}
        <InfoNote label="Where These Come From">
          The General Instructions for Forms W-2 and W-3 (2026). Box 1 includes a 2% shareholder&apos;s health insurance
          premiums; boxes 3 and 5 do not. Box 6 includes Additional Medicare Tax. Box 12&apos;s code DD, the cost of
          health coverage, is optional for an employer that filed fewer than 250 Forms W-2 for the year before, and is not
          used for a 2% shareholder&apos;s premiums (IRS, Form W-2 Reporting of Employer-Sponsored Health Coverage).
          Before filing, the instructions ask that Form W-3&apos;s boxes 2, 3 and 5 agree with the year&apos;s four Forms
          941.
        </InfoNote>
      </h1>

      {report !== null && shown !== undefined && (
        <div className="row report-period">
          <label>
            Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (report === null || report.year !== shown) && <p className="muted">Loading…</p>}
      {error === null && report !== null && report.year === shown && <Worksheet report={report} />}
    </section>
  );
}

function Worksheet({ report }: { report: W2Report }) {
  const drafts = report.draftRuns.length;
  return (
    <>
      <p className="notice">
        A worksheet: the figures to copy onto Forms W-2 and W-3 for {report.year}, from each employee&apos;s year to date.
        Nothing here files them. Copy A of each Form W-2 goes to the Social Security Administration with Form W-3, and
        the employee&apos;s copies to each employee, by {report.due.date}
        {report.due.date !== report.due.statutory && <>, the first business day after {report.due.statutory}</>}.
      </p>

      {!report.yearComplete && (
        <p className="notice notice-warn">
          {report.year} is not over: these are the figures so far, and pay still to come will change them.
        </p>
      )}

      {drafts > 0 && (
        <div className="notice notice-warn">
          {drafts === 1 ? `One run in ${report.year} is` : `${drafts} runs in ${report.year} are`} still a draft and on no
          form:
          <ul className="missed-list">
            {report.draftRuns.map((run) => (
              <li key={run.runId}>
                <Link to={`/payroll/runs/${run.runId}`}>
                  {run.employeeName}, paid {run.payDate}
                </Link>
              </li>
            ))}
          </ul>
          Post {drafts === 1 ? 'it' : 'them'}, then read this worksheet again.
        </div>
      )}

      {report.forms.length === 0 ? (
        <p className="muted">No pay is counted for {report.year}, so there is no Form W-2 to file.</p>
      ) : (
        <>
          {report.forms.map((form) => (
            <W2 key={form.employeeId} form={form} report={report} />
          ))}
          <W3 report={report} />
        </>
      )}
    </>
  );
}

/** Box b, or where to enter it. */
function employerEin(report: W2Report): ReactNode {
  return report.employerEin ?? <NotEntered what="EIN" />;
}

/** Box c: the name, then the address a line each, or where to enter it. */
function employerNameAndAddress(report: W2Report): ReactNode {
  return (
    <>
      <span className="w2-line">{report.employer}</span>
      {report.employerAddress.length === 0 ? (
        <NotEntered what="address" />
      ) : (
        report.employerAddress.map((line) => (
          <span key={line} className="w2-line">
            {line}
          </span>
        ))
      )}
    </>
  );
}

function NotEntered({ what }: { what: string }) {
  return (
    <span className="w2-line">
      No {what} entered: <Link to={settingsTab('business')}>Business Details in Settings</Link>
    </span>
  );
}

function W2({ form, report }: { form: W2Form; report: W2Report }) {
  const rows: Row[] = [
    ['a', "Employee's social security number", <Link to={`/payroll/employees/${form.employeeId}`}>On the Employee&apos;s Page</Link>],
    ['b', 'Employer identification number (EIN)', employerEin(report)],
    ['c', "Employer's name, address, and ZIP code", employerNameAndAddress(report)],
    ['e', "Employee's first name and initial", form.nameBoxes.firstNameAndInitial],
    ['e', 'Last name', form.nameBoxes.lastName],
    // The instructions: not on Copy A "unless the suffix appears on the card".
    ['e', 'Suff.', form.nameBoxes.suffix === null ? 'Leave blank' : `${form.nameBoxes.suffix}, if it is on the Social Security card`],
    [
      'f',
      "Employee's address and ZIP code",
      form.address.length === 0 ? (
        "None on file: add it on the employee's page"
      ) : (
        <>
          {form.address.map((line) => (
            <span key={line} className="w2-line">
              {line}
            </span>
          ))}
        </>
      ),
    ],
    ...amountRows(form.boxes),
    ['7 to 11', 'Tips, allocated tips, dependent care benefits, nonqualified plans', 'Leave blank'],
    ['12', 'Codes', 'Leave blank'],
    ['13', 'Statutory employee, retirement plan, third-party sick pay', 'Leave blank, unless the employee was in a retirement plan'],
    [
      '14a',
      'Other (optional)',
      form.box14a.length === 0
        ? 'Nothing to add'
        : form.box14a.map((item) => `${item.label}: ${formatCents(item.cents)}`).join('; '),
    ],
    ['15 to 17', 'State, state wages, state income tax', NO_STATE_TAX],
  ];
  return (
    <section className="settings-section" aria-label={`Form W-2, ${form.employeeName}`}>
      <h2>Form W-2: {form.employeeName}</h2>
      {form.checks.length > 0 && (
        <div className="notice notice-warn">
          Check before filing:
          <ul className="missed-list">
            {form.checks.map((check) => (
              <li key={check}>{check}</li>
            ))}
          </ul>
        </div>
      )}
      <Boxes label={`Form W-2 for ${form.employeeName}`} rows={rows} />
      <p className="muted">
        {form.employeeName}: {payCounted(form)}.
      </p>
      <FilledW2 form={form} year={report.year} />
    </section>
  );
}

/** The IRS's own W-2 for this employee, filled by the server (issue #46). */
function FilledW2({ form, year }: { form: W2Form; year: number }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setDownloading(true);
    setError(null);
    try {
      const file = await api.downloadW2Pdf(year, form.employeeId);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-w2-${year}-${form.nameBoxes.lastName.toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked later rather than at once, as the Form 941's download is.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not fill the form.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      {/* A block, not a flex row: the note's prose wraps under the button instead of squeezing it. */}
      <div>
        <button type="button" disabled={downloading} onClick={() => void download()}>
          {downloading ? 'Filling…' : 'Download Filled Form W-2'}
        </button>{' '}
        <InfoNote label="What the Filled W-2 Is For">
          The IRS's own Form W-2, fetched from irs.gov and filled with these boxes: Copies B, C and 2 to give{' '}
          {form.employeeName}, and Copy D for your records. Box a shows only the last four digits of the Social Security
          number, as the instructions allow on these copies. Copy A is left blank on purpose: the SSA cannot scan one
          printed from irs.gov, so file Copy A through the SSA&apos;s Business Services Online, typing it from the boxes
          above. Every field stays editable.
        </InfoNote>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </>
  );
}

function W3({ report }: { report: W2Report }) {
  const rows: Row[] = [
    ['b', 'Kind of payer', '941'],
    ['b', 'Kind of employer', 'None apply, for a business neither tax-exempt nor a government'],
    ['c', 'Total number of Forms W-2', String(report.transmittal.formCount)],
    ['e', 'Employer identification number (EIN)', employerEin(report)],
    ['f and g', "Employer's name, address, and ZIP code", employerNameAndAddress(report)],
    ...amountRows(report.transmittal.boxes),
    ['15 to 17', 'State, state wages, state income tax', NO_STATE_TAX],
  ];
  return (
    <section className="settings-section" aria-label="Form W-3">
      <h2>Form W-3</h2>
      <Boxes label="Form W-3" rows={rows} />
    </section>
  );
}
